import { UseQueryResult } from '@tanstack/react-query'
import { useMemo } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import { OffsetLimitPaginationResponse } from '~/api/common'
import { objectHookToContext } from '~/common/hookToContext'
import createDebugLogger from '~/common/logger'
import { useIsMounting } from '~/common/nextJsUtils'
import { ignoreProviderMissingError } from '~/common/reactUtils'
import { useActionBarQueryValues } from '~/components/v2/ResultsLayout/ActionBar/ActionBar.hook'
import { useFilterValues } from '~/components/v2/ResultsLayout/Filters/FiltersProvider'
import { useInternalListDataQueryResult } from '~/components/v2/ResultsLayout/ListQueryProvider/ListQueryProvider'
import {
  Pagination,
  PaginationControls,
  PaginationJumpTo,
  PaginationNextButton,
  PaginationPageNumbers,
  PaginationPreviousButton,
  PaginationTotalItems
} from '~/design_system'
import { DEFAULT_PAGINATION_RANGE } from '~/design_system/components/pagination/Pagination'
import { useHasAnyChangedOrMounted } from '~/hooks/usePrevious'
import { useNumberUrlState } from '~/hooks/useUrlState'

const debug = createDebugLogger('PaginationProvider')

interface PaginationProviderProps {
  limit?: number
  isEmbedded?: boolean
  resetPageDeps?: any[]
}

const DEFAULT_LIMIT = 30

export type PaginationApi = ReturnType<typeof usePagination>

function usePagination({ resetPageDeps, limit = DEFAULT_LIMIT, isEmbedded = false }: PaginationProviderProps) {
  const useUrlReadWrite = !isEmbedded
  const [page = 1, setPage] = useNumberUrlState('page', 1, useUrlReadWrite)

  useResetPageIfFiltersOrActionBarChange({ page, setPage, resetPageDeps })

  const offset = useMemo(() => page * limit - limit, [page, limit])

  // This variable is for passing to list api queries, it's a convenience to devs can just spread it into a query obj
  // { ...pagination.offsetLimit, ...otherQueryParams }
  const offsetLimit = useMemo(() => ({ offset, limit }), [offset, limit])

  return useMemo(
    () => ({
      page,
      offset,
      limit,
      offsetLimit,
      setPage,
      isEmbedded
    }),
    [page, offset, limit, offsetLimit, setPage, isEmbedded]
  )
}

function useResetPageIfFiltersOrActionBarChange({
  page,
  setPage,
  resetPageDeps = []
}: {
  page: number
  setPage: (page: number) => void
  resetPageDeps?: any[]
}) {
  const [filterValues] = ignoreProviderMissingError(useFilterValues)
  const [actionBar] = ignoreProviderMissingError(useActionBarQueryValues)

  // Reset page to 1 if the filters or action bar values changes after mount
  const isMounting = useIsMounting()
  const depsChanged = useHasAnyChangedOrMounted([filterValues, actionBar, ...resetPageDeps])
  if (depsChanged && page !== 1 && !isMounting) {
    debug('Resetting page to 1', { isMounting, depsChanged, page })
    setPage(1)
  }
}

type ResultsPaginationProps = {
  queryResult?: UseQueryResult<OffsetLimitPaginationResponse<any>>
  rangeSize?: number
  onPageChange?: (page: number) => void
}
export const ResultsPagination = ({
  queryResult: queryResultFromProps,
  rangeSize = DEFAULT_PAGINATION_RANGE,
  onPageChange
}: ResultsPaginationProps) => {
  const pagination = usePaginationContext()
  const queryResult = useInternalListDataQueryResult({ fallback: queryResultFromProps })

  if (!queryResult?.data || queryResult.data.total_pages === 0) {
    return null
  }

  return (
    <Pagination
      data-testid="results-pagination"
      currentPage={queryResult.data.page}
      onPageChange={page => {
        unstable_batchedUpdates(() => {
          onPageChange?.(page)
          pagination.setPage(page)
        })
        if (!pagination.isEmbedded) {
          window.scrollTo(0, 0)
        }
      }}
      totalPages={queryResult.data.total_pages}
      totalCount={queryResult.data.count}
      resultCount={queryResult.data.limit}
      rangeSize={rangeSize}>
      <PaginationControls>
        <PaginationPreviousButton />
        <PaginationPageNumbers />
        <PaginationNextButton />
        {queryResult.data.total_pages > rangeSize && <PaginationJumpTo />}
      </PaginationControls>
      <PaginationTotalItems />
    </Pagination>
  )
}

export const [PaginationProvider, usePaginationContext] = objectHookToContext(usePagination)

export const usePaginationOffsetLimit = () => {
  const context = usePaginationContext()
  return useMemo(() => ({ offset: context.offset, limit: context.limit }), [context.offset, context.limit])
}
